/**
 * The one run's «Perché» workbook — ONE per run (bata-reqs ADR 9002 stage 3, platform ADR 9050),
 * assembled here from the read API, because the platform sends CSV only (ADR 6194: a workbook
 * cannot be streamed) and describes the workbook instead: `GET /compacting/runs/{run_id}/workbook`
 * names its sheets, in order, and the read behind each.
 *
 *   «Movimenti»       the one merged list — proposed and held lines in the run's order, a state column
 *   «Trattenuti»      its held lines, each with the hold, the store that bound it and its cap
 *   «Rifiutati»       the candidates the run refused by name, each with its reason
 *   «Registro»        the store-week ledger: what each store sends and holds, kept and deferred
 *   «Parametri …»     the registry values in force for every key the run read, from its record —
 *                     never today's registry (bata-reqs ADR 9001 decision 5)
 *
 * The lines are read from the platform's CSV of each list (`GET …/export?kind=moves|refusals|ledger`,
 * one streamed request each, `./move-list-csv.ts`) — the rows of the JSON pages, row for row, without
 * walking them a page at a time (owner report 2026-11-02) — and names stand beside codes: stores and articles from the names run's (`names_run_id`) names read,
 * sizes from its network-health sizes (`./move-names.ts`). Codes stay in their own columns, as every
 * file of the app keeps them (DEC-106); the workbook says them in words (`display`), the CSV keeps
 * the codes. The CSV of this export is the first sheet's table.
 *
 * Money is the run's currency: a column in money where the run states one, a plain number otherwise —
 * never a currency guessed.
 */

import { callOperation, type CsvProgress } from '@/api';
import {
  getMoveRunParameters, getMoveRunReport, getMoveRunWorkbook, runNamesReport,
  type MoveLedgerItem, type MoveListItem, type MoveRunSummary, type MoveRunWorkbook, type RunNamesResponse,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, ExportTranslate, ProvenanceFilter, WorkbookSheet } from '@/export';
import { readConsolidationRunParameters, withParametersInForce, type ParametersInForce, type SizeName } from '@/planning';

import type { ExportScope } from '../../manifest';
import { indexNamesResponse, nameChunks, runNamesOf, type Named, type RunNames } from '../run-names';
import { readMoveLedger, readMoveLines } from './move-list-csv';
import { lineSize, moveSizeNamer, type SizeIndex } from './move-names';
import {
  amountOf, readMoveRunId, HOLD_KEY, PURPOSE_KEY, REFUSAL_KEY, STATE_HELD, STATE_KEY, STOCK_OWNER_KEY,
  WORKBOOK_SHEET_KEY, WORKBOOK_SHEETS, type WorkbookSheetId,
} from './move-run';

/** The export's id, as the route registers it. */
export const MOVE_WORKBOOK_EXPORT_ID = 'inseason.compactingReport.workbook';
export const MOVE_WORKBOOK_TITLE_KEY = 'inseason.compacting.workbook.title';

const MISSING: ExportCell = { kind: 'missing', state: 'not-reported' };
const NOT_APPLICABLE: ExportCell = { kind: 'missing', state: 'not-ranged' };

const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? MISSING : { kind: 'text', value });
const label = (code: string | null | undefined): ExportCell => (code === null || code === undefined || code === '' ? NOT_APPLICABLE : { kind: 'label', value: code, code });
const integer = (value: number | null | undefined): ExportCell => (value === null || value === undefined ? MISSING : { kind: 'integer', raw: value });
const number = (value: number | null): ExportCell => (value === null ? MISSING : { kind: 'number', raw: value });
const name = (one: Named): ExportCell => (one.name === null ? MISSING : { kind: 'text', value: one.name });

/** A code's words in the workbook; a code this build does not name stays as it is. */
function words(table: Readonly<Record<string, string>>) {
  return (code: string, translate: ExportTranslate): string => {
    const key = table[code];
    return key === undefined ? code : translate(key);
  };
}

/** Each column's header and guide sentence, spelled out so the catalogue check sees every key. */
const COLUMN_KEYS: Readonly<Record<string, readonly [string, string]>> = {
  list_rank: ['inseason.compacting.workbook.column.list_rank', 'inseason.compacting.workbook.columnGuide.list_rank'],
  state: ['inseason.compacting.workbook.column.state', 'inseason.compacting.workbook.columnGuide.state'],
  stock_owner: ['inseason.compacting.workbook.column.stock_owner', 'inseason.compacting.workbook.columnGuide.stock_owner'],
  purpose: ['inseason.compacting.workbook.column.purpose', 'inseason.compacting.workbook.columnGuide.purpose'],
  origin_store_name: ['inseason.compacting.workbook.column.origin_store_name', 'inseason.compacting.workbook.columnGuide.origin_store_name'],
  origin_store_key: ['inseason.compacting.workbook.column.origin_store_key', 'inseason.compacting.workbook.columnGuide.origin_store_key'],
  destination_store_name: ['inseason.compacting.workbook.column.destination_store_name', 'inseason.compacting.workbook.columnGuide.destination_store_name'],
  destination_store_key: ['inseason.compacting.workbook.column.destination_store_key', 'inseason.compacting.workbook.columnGuide.destination_store_key'],
  article_name: ['inseason.compacting.workbook.column.article_name', 'inseason.compacting.workbook.columnGuide.article_name'],
  article_key: ['inseason.compacting.workbook.column.article_key', 'inseason.compacting.workbook.columnGuide.article_key'],
  size_code: ['inseason.compacting.workbook.column.size_code', 'inseason.compacting.workbook.columnGuide.size_code'],
  size_scale: ['inseason.compacting.workbook.column.size_scale', 'inseason.compacting.workbook.columnGuide.size_scale'],
  size_key: ['inseason.compacting.workbook.column.size_key', 'inseason.compacting.workbook.columnGuide.size_key'],
  units: ['inseason.compacting.workbook.column.units', 'inseason.compacting.workbook.columnGuide.units'],
  net_benefit: ['inseason.compacting.workbook.column.net_benefit', 'inseason.compacting.workbook.columnGuide.net_benefit'],
  lane_cost: ['inseason.compacting.workbook.column.lane_cost', 'inseason.compacting.workbook.columnGuide.lane_cost'],
  run_damage: ['inseason.compacting.workbook.column.run_damage', 'inseason.compacting.workbook.columnGuide.run_damage'],
  carrying_cost_saved: ['inseason.compacting.workbook.column.carrying_cost_saved', 'inseason.compacting.workbook.columnGuide.carrying_cost_saved'],
  delta_units_sold: ['inseason.compacting.workbook.column.delta_units_sold', 'inseason.compacting.workbook.columnGuide.delta_units_sold'],
  expected_price: ['inseason.compacting.workbook.column.expected_price', 'inseason.compacting.workbook.columnGuide.expected_price'],
  landed_cost: ['inseason.compacting.workbook.column.landed_cost', 'inseason.compacting.workbook.columnGuide.landed_cost'],
  landed_cost_provenance: ['inseason.compacting.workbook.column.landed_cost_provenance', 'inseason.compacting.workbook.columnGuide.landed_cost_provenance'],
  realisation_rate: ['inseason.compacting.workbook.column.realisation_rate', 'inseason.compacting.workbook.columnGuide.realisation_rate'],
  hold_reason: ['inseason.compacting.workbook.column.hold_reason', 'inseason.compacting.workbook.columnGuide.hold_reason'],
  hold_store_key: ['inseason.compacting.workbook.column.hold_store_key', 'inseason.compacting.workbook.columnGuide.hold_store_key'],
  hold_cap: ['inseason.compacting.workbook.column.hold_cap', 'inseason.compacting.workbook.columnGuide.hold_cap'],
  owner_dc_units: ['inseason.compacting.workbook.column.owner_dc_units', 'inseason.compacting.workbook.columnGuide.owner_dc_units'],
  owner_threshold: ['inseason.compacting.workbook.column.owner_threshold', 'inseason.compacting.workbook.columnGuide.owner_threshold'],
  ship_week: ['inseason.compacting.workbook.column.ship_week', 'inseason.compacting.workbook.columnGuide.ship_week'],
  disposition: ['inseason.compacting.workbook.column.disposition', 'inseason.compacting.workbook.columnGuide.disposition'],
  line_id: ['inseason.compacting.workbook.column.line_id', 'inseason.compacting.workbook.columnGuide.line_id'],
  refusal_reason: ['inseason.compacting.workbook.column.refusal_reason', 'inseason.compacting.workbook.columnGuide.refusal_reason'],
  refusal_detail: ['inseason.compacting.workbook.column.refusal_detail', 'inseason.compacting.workbook.columnGuide.refusal_detail'],
  store_name: ['inseason.compacting.workbook.column.store_name', 'inseason.compacting.workbook.columnGuide.store_name'],
  store_key: ['inseason.compacting.workbook.column.store_key', 'inseason.compacting.workbook.columnGuide.store_key'],
  units_sent: ['inseason.compacting.workbook.column.units_sent', 'inseason.compacting.workbook.columnGuide.units_sent'],
  units_deferred: ['inseason.compacting.workbook.column.units_deferred', 'inseason.compacting.workbook.columnGuide.units_deferred'],
  moves_retained: ['inseason.compacting.workbook.column.moves_retained', 'inseason.compacting.workbook.columnGuide.moves_retained'],
  moves_deferred: ['inseason.compacting.workbook.column.moves_deferred', 'inseason.compacting.workbook.columnGuide.moves_deferred'],
  moves_cap_bound: ['inseason.compacting.workbook.column.moves_cap_bound', 'inseason.compacting.workbook.columnGuide.moves_cap_bound'],
  units_cap_bound: ['inseason.compacting.workbook.column.units_cap_bound', 'inseason.compacting.workbook.columnGuide.units_cap_bound'],
};

function column(key: string, kind: ExportColumn['kind'], extra: Partial<ExportColumn> = {}): ExportColumn {
  const [headerKey, descriptionKey] = COLUMN_KEYS[key] ?? [key, key];
  return { key, headerKey, descriptionKey, kind, ...extra };
}

/** A money column in the run's currency, or a plain number where the run states none. */
function money(key: string, currency: string | null, extra: Partial<ExportColumn> = {}): ExportColumn {
  return currency === null ? column(key, 'number', { decimals: 0, ...extra }) : column(key, 'money', { currency, decimals: 0, total: 'sum', ...extra });
}

/** A unit amount (a price, a cost per pair): money, never totalled. */
function unitMoney(key: string, currency: string | null): ExportColumn {
  return currency === null ? column(key, 'number', { decimals: 0 }) : column(key, 'money', { currency, decimals: 0 });
}

function moneyCell(value: string | null, currency: string | null): ExportCell {
  const amount = amountOf(value);
  if (amount === null) return MISSING;
  return currency === null ? { kind: 'number', raw: amount } : { kind: 'money', raw: amount, currency };
}

/** The columns of a line, shared by «Movimenti» and «Trattenuti». */
export function lineColumns(currency: string | null): readonly ExportColumn[] {
  return [
    column('list_rank', 'integer', { freeze: true }),
    column('state', 'label', { freeze: true, display: words(STATE_KEY) }),
    column('stock_owner', 'label', { display: words(STOCK_OWNER_KEY) }),
    column('purpose', 'label', { display: words(PURPOSE_KEY) }),
    column('origin_store_name', 'text'),
    column('origin_store_key', 'text'),
    column('destination_store_name', 'text'),
    column('destination_store_key', 'text'),
    column('article_name', 'text'),
    column('article_key', 'text'),
    column('size_code', 'text'),
    column('size_scale', 'text'),
    column('size_key', 'text'),
    column('units', 'integer', { total: 'sum' }),
    money('net_benefit', currency, { polarity: 'higher-is-better' }),
    money('lane_cost', currency, { polarity: 'lower-is-better' }),
    money('run_damage', currency, { polarity: 'lower-is-better' }),
    money('carrying_cost_saved', currency, { polarity: 'higher-is-better' }),
    column('delta_units_sold', 'number', { decimals: 2 }),
    unitMoney('expected_price', currency),
    unitMoney('landed_cost', currency),
    column('landed_cost_provenance', 'text'),
    column('realisation_rate', 'number', { decimals: 4 }),
    column('hold_reason', 'label', { display: words(HOLD_KEY) }),
    column('hold_store_key', 'text'),
    column('hold_cap', 'integer'),
    column('owner_dc_units', 'integer'),
    column('owner_threshold', 'integer'),
    column('ship_week', 'text'),
    column('disposition', 'text'),
    column('line_id', 'text'),
  ];
}

/** The names a file joins onto the lines. */
export interface MoveNames {
  readonly names: RunNames;
  readonly sizes: ReadonlyMap<string, SizeIndex>;
}

const store = (names: RunNames, code: string | null): ExportCell => (code === null ? NOT_APPLICABLE : name(names.store(code)));
const article = (names: RunNames, code: string | null): ExportCell => (code === null ? NOT_APPLICABLE : name(names.article(code)));
function sizeCells(size: SizeName): readonly [ExportCell, ExportCell] {
  return [size.code === null ? MISSING : { kind: 'text', value: size.code }, size.scale === null ? MISSING : { kind: 'text', value: size.scale }];
}

/** One line's cells, in {@link lineColumns}' order. Pure. */
export function lineCells(line: MoveListItem, joined: MoveNames, currency: string | null): readonly ExportCell[] {
  const size = lineSize(joined.sizes, line);
  return [
    integer(line.list_rank),
    label(line.state),
    label(line.stock_owner),
    label(line.purpose),
    store(joined.names, line.origin_store_key),
    text(line.origin_store_key),
    store(joined.names, line.destination_store_key),
    text(line.destination_store_key),
    article(joined.names, line.article_key),
    text(line.article_key),
    ...sizeCells(size),
    text(line.size_key),
    integer(line.units),
    moneyCell(line.net_benefit_amount, currency),
    moneyCell(line.lane_cost_amount, currency),
    moneyCell(line.run_damage_amount, currency),
    moneyCell(line.carrying_cost_saved_amount, currency),
    number(amountOf(line.delta_units_sold)),
    moneyCell(line.expected_price_amount, currency),
    moneyCell(line.landed_cost_amount, currency),
    text(line.landed_cost_provenance),
    number(amountOf(line.realisation_rate)),
    line.state === STATE_HELD ? label(line.hold_reason_code) : NOT_APPLICABLE,
    line.state === STATE_HELD ? text(line.hold_store_key) : NOT_APPLICABLE,
    line.state === STATE_HELD ? integer(line.hold_cap) : NOT_APPLICABLE,
    integer(line.owner_dc_units),
    integer(line.owner_threshold),
    text(line.ship_week),
    line.disposition === null ? NOT_APPLICABLE : text(line.disposition),
    text(line.line_id),
  ];
}

/** «Rifiutati»: each refused candidate, its reason first. */
export const REFUSED_COLUMNS: readonly ExportColumn[] = [
  column('refusal_reason', 'label', { freeze: true, display: words(REFUSAL_KEY) }),
  column('refusal_detail', 'text'),
  column('stock_owner', 'label', { display: words(STOCK_OWNER_KEY) }),
  column('purpose', 'label', { display: words(PURPOSE_KEY) }),
  column('origin_store_name', 'text'),
  column('origin_store_key', 'text'),
  column('destination_store_name', 'text'),
  column('destination_store_key', 'text'),
  column('article_name', 'text'),
  column('article_key', 'text'),
  column('size_code', 'text'),
  column('size_scale', 'text'),
  column('size_key', 'text'),
  column('units', 'integer', { total: 'sum' }),
  column('line_id', 'text'),
];

export function refusedCells(line: MoveListItem, joined: MoveNames): readonly ExportCell[] {
  const size = lineSize(joined.sizes, line);
  return [
    label(line.refusal_code),
    text(line.refusal_detail),
    label(line.stock_owner),
    label(line.purpose),
    store(joined.names, line.origin_store_key),
    text(line.origin_store_key),
    store(joined.names, line.destination_store_key),
    text(line.destination_store_key),
    article(joined.names, line.article_key),
    text(line.article_key),
    ...sizeCells(size),
    text(line.size_key),
    integer(line.units),
    text(line.line_id),
  ];
}

/** «Registro»: the store-week budget, one store per row. */
export const LEDGER_COLUMNS: readonly ExportColumn[] = [
  column('store_name', 'text', { freeze: true }),
  column('store_key', 'text', { freeze: true }),
  column('units_sent', 'integer', { total: 'sum' }),
  column('units_deferred', 'integer', { total: 'sum' }),
  column('moves_retained', 'integer', { total: 'sum' }),
  column('moves_deferred', 'integer', { total: 'sum' }),
  column('moves_cap_bound', 'integer'),
  column('units_cap_bound', 'integer'),
];

export function ledgerCells(row: MoveLedgerItem, names: RunNames): readonly ExportCell[] {
  return [
    name(names.store(row.store_key)),
    text(row.store_key),
    integer(row.units_sent),
    integer(row.units_deferred),
    integer(row.moves_retained),
    integer(row.moves_deferred),
    row.moves_cap_bound === null ? NOT_APPLICABLE : integer(row.moves_cap_bound),
    row.units_cap_bound === null ? NOT_APPLICABLE : integer(row.units_cap_bound),
  ];
}

/** What the workbook is built from: the reads, already made. */
export interface MoveWorkbookReads {
  readonly run: MoveRunSummary;
  /** The platform's description of the workbook: which sheets, in which order. */
  readonly descriptor: MoveRunWorkbook | null;
  readonly lines: readonly MoveListItem[];
  readonly refused: readonly MoveListItem[];
  readonly ledger: readonly MoveLedgerItem[];
  readonly parameters: ParametersInForce;
  readonly joined: MoveNames;
}

/** The sheets in the platform's order; a sheet the descriptor does not name is left out, one it names that this build does not know too. Pure. */
export function sheetOrder(descriptor: MoveRunWorkbook | null): readonly WorkbookSheetId[] {
  if (descriptor === null) return WORKBOOK_SHEETS;
  const known = new Set<string>(WORKBOOK_SHEETS);
  return descriptor.sheets.map((sheet) => sheet.sheet).filter((sheet): sheet is WorkbookSheetId => known.has(sheet));
}

/** The run's workbook as an export request: the data sheets, then the parameters in force. Pure. */
export function moveWorkbookRequest(reads: MoveWorkbookReads): ExportRequest {
  const { run, joined } = reads;
  const currency = run.currency_code;
  const columns = lineColumns(currency);
  const held = reads.lines.filter((line) => line.state === STATE_HELD);
  const built: Record<Exclude<WorkbookSheetId, 'parameters'>, WorkbookSheet> = {
    moves: { titleKey: WORKBOOK_SHEET_KEY.moves, columns, rows: () => reads.lines.map((line) => lineCells(line, joined, currency)) },
    held: { titleKey: WORKBOOK_SHEET_KEY.held, columns, rows: () => held.map((line) => lineCells(line, joined, currency)) },
    refused: { titleKey: WORKBOOK_SHEET_KEY.refused, columns: REFUSED_COLUMNS, rows: () => reads.refused.map((line) => refusedCells(line, joined)) },
    ledger: { titleKey: WORKBOOK_SHEET_KEY.ledger, columns: LEDGER_COLUMNS, rows: () => reads.ledger.map((row) => ledgerCells(row, joined.names)) },
  };
  const order = sheetOrder(reads.descriptor);
  const sheets = order.filter((sheet): sheet is Exclude<WorkbookSheetId, 'parameters'> => sheet !== 'parameters').map((sheet) => built[sheet]);
  const filters: readonly ProvenanceFilter[] = [
    { field: 'move_run_id', operator: 'equals', value: run.run_id },
    { field: 'country_key', operator: 'equals', value: run.country_key },
    { field: 'ship_week', operator: 'equals', value: run.ship_week },
  ];
  const request: ExportRequest = {
    id: MOVE_WORKBOOK_EXPORT_ID,
    titleKey: MOVE_WORKBOOK_TITLE_KEY,
    columns,
    rows: () => reads.lines.map((line) => lineCells(line, joined, currency)),
    rowCount: reads.lines.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: run.stock_as_of_date === null ? NOT_PUBLISHED : { from: run.stock_as_of_date, to: run.stock_as_of_date },
      // A simulation of the one run is the file's scenario; a live run has none to state.
      scenario: run.simulation_id === null ? NOT_PUBLISHED : run.simulation_id,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: {
      filters,
      sort: [{ key: 'list_rank', direction: 'asc' }],
      visibleColumns: columns.map((one) => one.key),
      drillPath: [],
      densityMode: 'compact',
    },
    workbook: { sheets },
  };
  return order.includes('parameters') ? withParametersInForce(request, reads.parameters) : request;
}

/** The names a file joins onto its lines, asked for while its lists are still arriving. */
interface MoveNamer {
  /** Lines the file will hold: their stores', articles' and sizes' names are asked for now. */
  readonly lines: (lines: readonly MoveListItem[]) => void;
  /** Stores the file will hold (the ledger's). */
  readonly stores: (keys: readonly string[]) => void;
  /** Every name, once every ask has answered. */
  readonly finish: () => Promise<MoveNames>;
}

/**
 * The names run's names of every store and article the lines name, in chunks, and their sizes
 * (`./move-names.ts`). Each list's keys are asked for the moment that list arrives, never after the
 * last one; a key is asked for once. A failed names read names nothing, as before.
 */
function moveNamer(namesRunId: string | null): MoveNamer {
  const sizes = moveSizeNamer(namesRunId);
  const storesAsked = new Set<string>();
  const articlesAsked = new Set<string>();
  const answers: Promise<RunNamesResponse>[] = [];
  const fresh = (keys: readonly (string | null)[], asked: Set<string>): string[] => {
    const out: string[] = [];
    for (const key of keys) {
      if (key === null || asked.has(key)) continue;
      asked.add(key);
      out.push(key);
    }
    return out;
  };
  const ask = (stores: readonly (string | null)[], articles: readonly (string | null)[]): void => {
    if (namesRunId === null) return;
    for (const chunk of nameChunks(fresh(stores, storesAsked), fresh(articles, articlesAsked))) {
      const answer = callOperation(runNamesReport, {
        path: { run_id: namesRunId },
        query: {
          ...(chunk.stores.length === 0 ? {} : { store_key: chunk.stores }),
          ...(chunk.articles.length === 0 ? {} : { article_key: chunk.articles }),
        },
      });
      // Awaited in `finish`; when a list fails first, `finish` is never reached and the rejection must not go unheard.
      answer.catch(() => undefined);
      answers.push(answer);
    }
  };
  return {
    lines: (lines) => {
      ask(lines.flatMap((line) => [line.origin_store_key, line.destination_store_key]), lines.map((line) => line.article_key));
      sizes.add(lines);
    },
    stores: (keys) => { ask(keys, []); },
    finish: async () => {
      const [names, sized] = await Promise.all([
        namesRunId === null
          ? runNamesOf(null, 'unreadable')
          : Promise.all(answers).then((all) => runNamesOf(indexNamesResponse(all), 'ready'), () => runNamesOf(null, 'unreadable')),
        sizes.finish(),
      ]);
      return { names, sizes: sized };
    },
  };
}

/**
 * Every read the workbook needs, made now — a button was pressed. Owner report 2026-11-02 (very slow):
 * each list is now ONE streamed request (`./move-list-csv.ts`) instead of a walk of JSON pages one
 * after another, the five reads start together, each list's names are asked for as soon as it arrives,
 * and a size is asked for only until it is named (`moveSizeNamer`).
 */
export async function readMoveWorkbook(run: MoveRunSummary): Promise<MoveWorkbookReads> {
  const namer = moveNamer(run.names_run_id);
  const [descriptor, lines, refused, ledger, parameters] = await Promise.all([
    callOperation(getMoveRunWorkbook, { path: { run_id: run.run_id } }).catch(() => null),
    readMoveLines(run.run_id, 'moves').then((found) => { namer.lines(found); return found; }),
    readMoveLines(run.run_id, 'refusals').then((found) => { namer.lines(found); return found; }),
    readMoveLedger(run.run_id).then((found) => { namer.stores(found.map((row) => row.store_key)); return found; }),
    readConsolidationRunParameters(run.run_id, getMoveRunParameters),
  ]);
  return { run, descriptor, lines, refused, ledger, parameters, joined: await namer.finish() };
}

/** What the CSV — the merged list's own table — is built from: the run, its lines and their names. */
export interface MoveListCsvReads {
  readonly run: MoveRunSummary;
  readonly lines: readonly MoveListItem[];
  readonly joined: MoveNames;
}

/**
 * The CSV's reads: the one list in ONE request and its own lines' names — nothing of what only the
 * workbook's other sheets hold (the refused candidates, the ledger, the parameters, the descriptor).
 * `onProgress` hears the rows as they arrive.
 */
export async function readMoveListCsv(run: MoveRunSummary, onProgress?: CsvProgress): Promise<MoveListCsvReads> {
  const namer = moveNamer(run.names_run_id);
  const lines = await readMoveLines(run.run_id, 'moves', onProgress);
  namer.lines(lines);
  return { run, lines, joined: await namer.finish() };
}

/**
 * The CSV of the run: the first sheet's table, column for column and row for row the workbook's
 * (`moveWorkbookRequest`), with the same provenance and perimeter. The CSV writes no other sheet, so
 * none is read. Pure.
 */
export function moveListCsvRequest(reads: MoveListCsvReads): ExportRequest {
  return moveWorkbookRequest({
    run: reads.run, descriptor: null, lines: reads.lines, refused: [], ledger: [],
    parameters: { state: 'absent', runId: reads.run.run_id, reason: 'unread' }, joined: reads.joined,
  });
}

/**
 * The route's export — the run in the URL's workbook (its CSV, the merged list). With no run in the
 * URL the file says so in its provenance and holds no line: nothing is defaulted in a file.
 */
export async function moveWorkbookExport(given?: ExportScope): Promise<ExportRequest> {
  const runId = readMoveRunId(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  if (runId === null) {
    return moveWorkbookRequest({
      run: NO_MOVE_RUN, descriptor: null, lines: [], refused: [], ledger: [],
      parameters: { state: 'absent', runId: null, reason: 'no_run' }, joined: { names: runNamesOf(null, 'unreadable'), sizes: new Map() },
    });
  }
  const report = await callOperation(getMoveRunReport, { path: { run_id: runId } });
  return moveWorkbookRequest(await readMoveWorkbook(report.run));
}

/** The header a file with no run states: every field empty, the run id blank. */
const NO_MOVE_RUN: MoveRunSummary = {
  articles_compacting: null, articles_replenishment: null, as_of: '', candidate_run_id: null, names_run_id: null, compacting_dc_units_threshold: null,
  country_key: '', currency_code: null, held_lines: 0, job_run_id: null, lines: 0, move_valuation: null, proposed_lines: 0,
  recorded_at: '', refusal_code: null, refused_candidates: 0, run_id: '', ship_week: '', status: '', stock_as_of_date: null,
  stock_batch_id: null, superseded: false, superseded_by_run_id: null, threshold_change_id: null, simulation_id: null,
};

/** The merged list's page registers the same workbook under its own id (`export-resolve-testing.ts`). */
export const MOVE_LIST_EXPORT_ID = 'inseason.compactingMoves.workbook';

export async function moveListExport(given?: ExportScope): Promise<ExportRequest> {
  return { ...(await moveWorkbookExport(given)), id: MOVE_LIST_EXPORT_ID };
}
